/**
 * MatterService
 *
 * This service handles all matter-related operations in the application.
 * It provides methods for managing legal matters, including creation, retrieval,
 * updating, and status tracking. The service supports operations for both
 * individual matters and system-wide matter management.
 *
 * @class
 */

import {
  pathMyMattersEmployeeId,
  pathMatterById,
  pathAddMatter,
  pathMatterName,
  pathMatterStatusDistribution,
  pathAllMatters,
  pathBilledTime,
  pathApiMatters,
  pathApiMatterId,
  pathEditMatter,
  pathRenameMatter,
  pathDeleteMatter,
  pathUpdateAssociatedEmployees,
  pathUpdateMatterNote,
  pathDeleteMatterNote,
  pathMatterDataMatterId,
} from '@frontend/__settings__/septSoftwarePaths';
import HttpService from './HttpService';
import {
  MyMattersEmployeeIdResponse,
  GetAllMattersResponse,
  GetMatterByIdResponse,
  MatterNameByMatterIdResponse,
  CreateMatterRequest,
  CreateMatterResponse,
  MatterStatusDistributionResponse,
  AddMatterBilledTimeResponse,
  TypedMatterListItem,
  TypedMattersByEmployeeResponse,
  CreateProjectMatterRequest,
  CreateProjectMatterResponse,
  EditMatterResponse,
  EditMatterRequest,
  RenameMatterRequest,
  RenameMatterResponse,
  DeleteProjectMatterResponse,
  UpdateAssociatedEmployeesRequest,
  UpdateAssociatedEmployeesResponse,
  UpdateMatterNoteRequest,
  UpdateMatterNoteResponse,
  DeleteMatterNoteResponse,
  DeleteMatterResponse,
} from '@frontend/Models/Matter/MatterRequestResponseModels';
import { MatterList } from '@frontend/Models/Matter/MatterModels';
import { getProjectTypeCode } from '@frontend/AppSettings/environmentConfig';

const http = new HttpService();
class MatterService {
  /**
   * Retrieves matters assigned to a specific employee
   *
   * @param {string} employeeId - ID of the employee
   * @returns {Promise<Array>} Array of matters assigned to the employee
   * @throws {Error} If the API request fails
   */
  static async getMattersByEmployee(
    employeeId: string
  ): Promise<MyMattersEmployeeIdResponse> {
    const response = await http.get<MyMattersEmployeeIdResponse>(
      pathMyMattersEmployeeId(employeeId)
    );
    return response;
  }

  /**
   * Retrieves all matters from the system
   *
   * @returns {Promise<Array>} Array of all matters, sorted by matter ID
   * @throws {Error} If the API request fails
   */
  static async getMatters(): Promise<MatterList> {
    const response = await http.get<GetAllMattersResponse>(pathAllMatters);
    if (!response.successful || !response.data) {
      throw new Error('An error occurred while fetching matters.');
    }

    // sort the data by matterid
    const sortedData = response.data.sort((a, b) =>
      a.matterid.localeCompare(b.matterid)
    );

    return sortedData;
  }

  /**
   * Retrieves a page of matters assigned to a specific employee (server-side paginated)
   */
  static async getMattersByEmployeePaginated(
    employeeId: string,
    params: { page: number; limit: number; search?: string; status?: string; sortBy?: string; sortOrder?: string }
  ): Promise<{ items: MyMattersEmployeeIdResponse[]; total: number; page: number; limit: number }> {
    const query = new URLSearchParams();
    query.set('page', String(params.page));
    query.set('limit', String(params.limit));
    if (params.search) query.set('search', params.search);
    if (params.status) query.set('status', params.status);
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortOrder) query.set('sortOrder', params.sortOrder);

    const response = await http.get<{
      successful: boolean;
      data: { items: MyMattersEmployeeIdResponse[]; total: number; page: number; limit: number };
    }>(`${pathMyMattersEmployeeId(employeeId)}?${query.toString()}`);

    const data = (response as any)?.data ?? response;
    return data as { items: MyMattersEmployeeIdResponse[]; total: number; page: number; limit: number };
  }

  /**
   * Retrieves a page of all matters (server-side paginated)
   */
  static async getMattersPaginated(
    params: { page: number; limit: number; search?: string; status?: string; client?: string; leader?: string; sortBy?: string; sortOrder?: string }
  ): Promise<{ items: MatterList; total: number; page: number; limit: number }> {
    const query = new URLSearchParams();
    query.set('page', String(params.page));
    query.set('limit', String(params.limit));
    if (params.search) query.set('search', params.search);
    if (params.status) query.set('status', params.status);
    if (params.client) query.set('client', params.client);
    if (params.leader) query.set('leader', params.leader);
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortOrder) query.set('sortOrder', params.sortOrder);

    const response = await http.get<{
      successful: boolean;
      data: { items: MatterList; total: number; page: number; limit: number };
    }>(`${pathAllMatters}?${query.toString()}`);

    const data = (response as any)?.data ?? response;
    return data as { items: MatterList; total: number; page: number; limit: number };
  }

  /**
   * Retrieves detailed information about a specific matter
   *
   * @param {string} matterId - ID of the matter
   * @returns {Promise<Object>} Matter details
   * @throws {Error} If the API request fails
   */
  static async getMatterDetails(
    matterId: string
  ): Promise<GetMatterByIdResponse> {
    const response = await http.get<GetMatterByIdResponse | { successful?: boolean; data?: GetMatterByIdResponse }>(
      pathMatterById(matterId)
    );
    const data = (response as { successful?: boolean; data?: GetMatterByIdResponse })?.data ?? response;
    return data as GetMatterByIdResponse;
  }

  /**
   * Retrieves the name of a matter by its ID
   *
   * @param {string} matterId - ID of the matter
   * @returns {Promise<string>} Name of the matter
   * @throws {Error} If the API request fails
   */
  static async getMatterName(
    matterId: string
  ): Promise<MatterNameByMatterIdResponse | undefined> {
    if (!matterId) {
      return;
    }
    const response = await http.get<{ successful?: boolean; data?: MatterNameByMatterIdResponse }>(
      pathMatterName(matterId)
    );
    if (response?.successful && response.data) {
      return response.data;
    }
    return undefined;
  }

  /**
   * Creates a new matter
   *
   * @param {Object} formData - Matter data
   * @returns {Promise<Object>} Created matter object
   * @throws {Error} If the API request fails
   */
  static async addMatter(
    matterData: CreateMatterRequest
  ): Promise<CreateMatterResponse> {
    const response = await http.post<CreateMatterResponse>(
      pathAddMatter,
      matterData
    );
    return response;
  }

  /**
   * Retrieves the distribution of matter statuses
   *
   * @returns {Promise<Object>} Distribution of matter statuses
   * @throws {Error} If the API request fails
   */
  static async getMatterStatusDistribution(): Promise<MatterStatusDistributionResponse> {
    const data = await http.get<MatterStatusDistributionResponse>(
      pathMatterStatusDistribution
    );
    return data;
  }

  /**
   * Submits billed time for a matter
   *
   * @param {Object} submissionData - Billed time data
   * @returns {Promise<Object>} Response indicating success or failure
   * @throws {Error} If the API request fails
   */
  static async submitBilledTime(
    submissionData: any
  ): Promise<AddMatterBilledTimeResponse> {
    const data = await http.post<AddMatterBilledTimeResponse>(
      pathBilledTime,
      submissionData
    );
    return data;
  }

  /**
   * Retrieves the engineering vertical's project matters (P3: GET /api/matters?type=…,
   * the tenant's configured project-type code — never a client-named route).
   * @returns {Promise<Array>} Array of project matters
   */
  static async getProjectMatters(): Promise<TypedMatterListItem[]> {
    const type = encodeURIComponent(getProjectTypeCode());
    const response = await http.get<{ successful: boolean; data: TypedMatterListItem[] }>(
      `${pathApiMatters}?type=${type}`
    );
    if (!response.successful || !response.data) {
      throw new Error('An error occurred while fetching projects.');
    }
    return response.data;
  }

  /**
   * Retrieves project matters assigned to a specific employee (leader or associated).
   * @param employeeId the employee ID
   *
   * @returns {Promise<Array>} Array of project matters for the employee
   */
  static async getProjectMattersByEmployee(
    employeeId: string
  ): Promise<TypedMattersByEmployeeResponse> {
    const type = encodeURIComponent(getProjectTypeCode());
    const response = await http.get<{ successful: boolean; data: TypedMattersByEmployeeResponse }>(
      `${pathApiMatters}?type=${type}&assigned_to=${encodeURIComponent(employeeId)}`
    );
    if (!response.successful || !response.data) {
      throw new Error('An error occurred while fetching projects by employee.');
    }
    return response.data;
  }

  /**
   * Creates a new project matter through POST /api/matters. The tenant's configured
   * project-type code is stamped here so no calling surface carries a type literal.
   * @param matterData the matter data
   * @returns
   */
  static async createProjectMatter(
    matterData: CreateProjectMatterRequest
  ): Promise<CreateProjectMatterResponse> {
    const response = await http.post<{ successful: boolean; data: CreateProjectMatterResponse; error?: string }>(
      pathApiMatters,
      { ...matterData, type: getProjectTypeCode() }
    );
    if (!response.successful || !response.data) {
      // Surface the server's own message: the most likely refusal here is "unknown or
      // inactive matter type", which a generic string would leave undiagnosable.
      throw new Error(response.error || 'An error occurred while creating the project.');
    }
    return response.data;
  }

  /**
   * this function updates an existing matter.
   *
   * @param matterId The matter ID
   * @param updateData the update data
   * @returns
   */
  static async updateMatter(
    matterId: string,
    updateData: EditMatterRequest
  ): Promise<EditMatterResponse> {
    const data = await http.put<EditMatterResponse>(
      pathEditMatter(matterId),
      updateData
    );
    return data;
  }

  /**
   * Renames a matter's ID. The backend cascades the change to every table
   * that references the old matter ID (tasks, billing, etc.).
   *
   * @param matterId The current matter ID
   * @param newMatterId The new matter ID
   * @throws {HttpError} status 409 if the new ID is already taken
   */
  static async renameMatter(
    matterId: string,
    newMatterId: string
  ): Promise<RenameMatterResponse> {
    const body: RenameMatterRequest = { new_matterid: newMatterId };
    const data = await http.put<RenameMatterResponse>(
      pathRenameMatter(matterId),
      body
    );
    return data;
  }

  /**
   * Deletes a project matter and all its related data (DELETE /api/matters/<id>;
   * the backend refuses anything that is not an active engineering-vertical type).
   * @param matterId the matter ID
   * @returns
   */
  static async deleteProjectMatterById(
    matterId: string
  ): Promise<DeleteProjectMatterResponse> {
    const response = await http.delete<{ successful: boolean; data: DeleteProjectMatterResponse }>(
      pathApiMatterId(matterId)
    );
    if (!response.successful || !response.data) {
      throw new Error('An error occurred while deleting the project.');
    }
    return response.data;
  }

  /**
   * This function updates the employees associated with a matter.
   * @param matterId the matter ID
   * @param operation the operation "add" or "remove"
   * @param employeeIds the employee IDs to add or remove
   * @returns
   */
  static async updateAssociatedEmployees(
    matterId: string,
    operation: 'add' | 'remove',
    employeeIds: string[]
  ) {
    const update: UpdateAssociatedEmployeesRequest = {
      operation,
      employee_ids: employeeIds,
    };
    const data = await http.put<UpdateAssociatedEmployeesResponse>(
      pathUpdateAssociatedEmployees(matterId),
      update
    );
    return data;
  }

  /**
   * This is the function to update a matter note.
   * @param matterId the matter ID
   * @param noteId the note ID
   * @param noteText the note text
   * @returns
   */
  static async updateMatterNote(
    matterId: string,
    noteId: string,
    noteText: string
  ): Promise<UpdateMatterNoteResponse> {
    const update: UpdateMatterNoteRequest = {
      note_text: noteText,
    };
    const data = await http.put<any>(
      pathUpdateMatterNote(matterId, noteId),
      update
    );
    return data;
  }

  /**
   * This function deletes a matter note.
   * @param matterId the matter ID
   * @param noteId the note ID
   * @returns
   */
  static async deleteMatterNote(
    matterId: string,
    noteId: string
  ): Promise<DeleteMatterNoteResponse> {
    const data = await http.delete<DeleteMatterNoteResponse>(
      pathDeleteMatterNote(matterId, noteId)
    );
    return data;
  }

  /**
   * Deletes a matter and all its associated tasks
   *
   * @param {string} matterId - ID of the matter to delete
   * @returns {Promise<Object>} Response indicating success or failure
   * @throws {Error} If the API request fails
   */
  static async deleteMatter(matterId: string): Promise<DeleteMatterResponse> {
    const data = await http.delete<DeleteMatterResponse>(
      pathDeleteMatter(matterId)
    );
    return data;
  }

  /**
   * This gets the matter data
   * @param matterId the id to get matter data from.
   * @returns
   */
  static async getMatterData(matterId: string): Promise<any> {
    const data = await http.get<any>(pathMatterDataMatterId(matterId));
    return data;
  }
}

export default MatterService;
